import axios from "axios";
import n from "country-js";
import XLSX from "xlsx";
import countries from "./countries.json";
import languages from "./languages.json";
import { emailRegex } from "./regExps";
import { isLastCustomerEtaLate } from "./routeTime";
import { headerRow, LANDING_URL } from "../components/data/constants";

const MINUTE_DIVIDER = 60 * 1000;

const convertTimeToMinutes = time => Math.floor(time / MINUTE_DIVIDER);

const convertFromStartToEndDateMinutes = (startTime, endTime) =>
  convertTimeToMinutes(endTime) - convertTimeToMinutes(startTime);

export const addZero = num => (num < 10 ? "0" + num : num);

export const checkIsURL = text => {
  try {
    return new URL(text) && true;
  } catch (e) {
    return false;
  }
};

// Get country name from ISO two letter code
export function getCountryName(countryCode) {
  const country =
    countryCode &&
    countries.find(country => country[0] === countryCode.toUpperCase());
  if (country) {
    return country[1];
  } else {
    return countryCode;
  }
}

export const downloadStops = (
  stops,
  stopType = "project",
  isPastProject = false
) => {
  const finalHeaderRow = [
    ...headerRow,
    ...(headerRow
      ? [
          "delivery_status",
          "delivered_at",
          "external_note",
          "internal_note",
          "pod_age",
          "pod_signature",
          "pod_photo"
        ]
      : [])
  ];

  const stopsForXLSX = [
    finalHeaderRow,
    ...stops
      .filter(
        task =>
          !["start", "end", "break"].includes(task.type) && !isEmptyObj(task)
      )
      .reduce((array, row) => {
        return [
          ...array,
          ...(!row.units || row.units.length === 0
            ? [generateStopDataForXLSX(row, 0, { isPastProject })]
            : row.units.map((unit, i) =>
                generateStopDataForXLSX(row, i, { isPastProject })
              ))
        ];
      }, [])
  ];

  exportToXlsx(`${stopType}_stops.xlsx`, stopsForXLSX);
};

// Convert time string to seconds
export const timeToSeconds = str => {
  if (!str) return null;
  const timeArr = str.split(":");
  const seconds = timeArr[0] * 3600 + timeArr[1] * 60;

  return seconds;
};

export const isValidDurationForSelectedTime = (startFrom, endTo, duration) =>
  convertFromStartToEndDateMinutes(startFrom, endTo) > duration;

export function arraysEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  function sort(arr) {
    return arr.concat().sort();
  }
  const sortedA = sort(a);
  const sortedB = sort(b);

  for (var i = 0; i < a.length; ++i) {
    if (sortedA[i] !== sortedB[i]) return false;
  }
  return true;
}

// Convert seconds to time string
export const secondsConvert = seconds => {
  // if (!seconds) return null;
  let hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  hours = hours % 24;
  return new Date(new Date().setHours(hours, minutes));
};

export const secondsToTime = seconds => {
  let hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  hours = hours % 24;
  return !isNaN(hours) ? `${addZero(hours)}:${addZero(minutes)}` : "";
};

export const makeTextPlural = count => (count > 1 ? "s" : "");

// Get hours and minutes
export const getHoursMins = str => {
  if (!str) return null;
  const hours = new Date(str).getHours();
  const minutes = new Date(str).getMinutes();
  return !isNaN(hours) && !isNaN(minutes)
    ? `${addZero(hours)}:${addZero(minutes)}`
    : "";
};

export const convertTimeToHoursAndMins = str => {
  const trimmedStr = str.replace(/\s+/g, "");
  const modifier = trimmedStr.slice(-2).toUpperCase();
  let hours, minutes;

  if (["AM", "PM"].includes(modifier)) {
    const time = trimmedStr.slice(0, -2);
    [hours, minutes] = time.split(":").slice(0, 2);

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
    }
  } else {
    [hours, minutes] = str.split(":").slice(0, 2);
  }

  return `${hours}:${minutes}`;
};

// Get right type date value from date/milliseconds string
export const getDateValue = str => {
  return str && !isNaN(+str) ? +str : str;
};

// Convert seconds to hours mins
export const hoursMinsFromSeconds = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);

  return `${hours ? `${hours}h` : ""}${minutes}m`;
};

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

// Get day, month year of date
export const getDateParams = date => {
  const dateObj = new Date(date);
  const month = months[dateObj.getMonth()];
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  return { day, month, year };
};

// Format number 1000 -> 1,000
export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export const formatCurrency = (value, currency) => {
  const currencySymbols = {
    eur: "€",
    gbp: "£",
    usd: "$"
  };
  return `${currencySymbols[currency]}${numberWithCommas(
    Math.floor(value / 100)
  )}.${value % 100}`;
};

// Convert seconds to time string
export const parseDateStr = dateStr => {
  const date = new Date(dateStr);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const day = date.getDate();
  const month = months[date.getMonth()];

  return !isNaN(hours)
    ? `${addZero(hours)}:${addZero(minutes)}, ${day} ${month}`
    : "";
};

export const isEmptyObj = obj => {
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) return false;
  }
  return true;
};

// Capitalize first letter
export const capitalize = txt =>
  txt ? txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase() : "";

// Get distance string KM or MI
export const distanceStr = (meters, isKm, isDownload) => {
  const distanceCalculation = meters / 1000 / (isKm ? 1 : 1.60934);
  let longDistance = Number(Math.floor(distanceCalculation));
  let shortDistance = Number(distanceCalculation.toFixed(1));

  return `${distanceCalculation < 1 ? shortDistance : longDistance}${
    isDownload ? "" : isKm ? "km" : "mi"
  }`;
};

export const remCalc = num => `${Number((num / 16).toFixed(4))}rem`;

// Validate email
export const validateEmail = email => {
  return emailRegex.test(String(email).toLowerCase());
};

export const getRouteUnitsLabel = unitsCount =>
  unitsCount > 0 ? `${unitsCount}u` : "";

export const validateCapacity = str => {
  str = str.trim();
  if (!str) {
    return false;
  }
  str = str.replace(/^0+/, "") || "0";
  var n = Math.floor(Number(str));
  return n !== Infinity && String(n) === str && n > 0;
};

export const getUrlParams = param => {
  let paramValue;

  try {
    var urlParams = new URLSearchParams(window.location.search);
    paramValue = urlParams.get(param);
  } catch (e) {
    var queryParams = window.location.search
      .substr(1)
      .split("&")
      .reduce(function (q, query) {
        var chunks = query.split("=");
        var key = chunks[0];
        var value = chunks[1];
        q[key] = value;
        return q;
      }, {});
    paramValue = queryParams[param];
  }

  return paramValue;
};

export const trimSpaces = str => {
  return str ? str.replace(/\s+/g, "") : str;
};

export const checkCurrencyAndCountry = async () => {
  const res = await axios.get("https://www.cloudflare.com/cdn-cgi/trace");
  const data = res.data.split("\n").find(line => line.includes("loc"));
  const countryInfo = data && n.search(data.slice(4))[0];
  const currencyStr = countryInfo && countryInfo.currency.currencyCode;
  if (currencyStr === "GBP") {
    return [{ symbol: "£", string: currencyStr }, countryInfo];
  } else if (currencyStr === "EUR") {
    return [{ symbol: "€", string: currencyStr }, countryInfo];
  } else {
    return [{ symbol: "$", string: "USD" }, countryInfo];
  }
};

export const getCountryIdByLanguage = () => {
  const language = window.navigator.userLanguage || window.navigator.language;
  const browserLang = languages.find(
    item => item.language === language.toLowerCase()
  );

  return browserLang ? browserLang.country : "us";
};

export const getFormattedPhoneAndCountry = input => {
  const numberVal = input.replace(/\+| |\(|\)/g, "");
  const countryCode =
    input.indexOf(" ") >= 0 ? input.substr(0, input.indexOf(" ")) : input;
  const countryCodeVal = countryCode && countryCode.replace(/\+| |\(|\)/g, "");

  return { numberVal, countryCodeVal };
};

// Function sorting by shift time, passed to route groups array's sort method
export const sortRouteGroupsFn = (a, b) => {
  const startTimeA = a.split("-")[0];
  const endTimeA = a.split("-")[1];
  const startTimeB = b.split("-")[0];
  const endTimeB = b.split("-")[1];
  if (startTimeA > startTimeB) {
    return 1;
  } else if (startTimeA === startTimeB) {
    if (endTimeA === "null") {
      return 1;
    } else if (endTimeB === "null") {
      return -1;
    } else {
      return endTimeA > endTimeB ? 1 : -1;
    }
  } else {
    return -1;
  }
};

export const objectsEqual = (o1, o2) =>
  Object.keys(o1).length === Object.keys(o2).length &&
  Object.keys(o1).every(p => o1[p] === o2[p]);

export const arraysOfObjectsEqual = (a1, a2) =>
  a1.length === a2.length && a1.every((o, idx) => objectsEqual(o, a2[idx]));

// No negative, decimal, 0 value on key press
export const handleKeypress = e => {
  const characterCode = e.key;
  const code = e.keyCode;
  const characterNumber = Number(characterCode);
  if (characterNumber >= 0 && characterNumber <= 9) {
    if (e.target.value && e.target.value.length) return;
    // } else if (characterNumber === 0) {
    //   e.preventDefault();
    // }
  } else if (
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    ((code === 65 || code === 67 || code === 86 || code === 88) &&
      (e.ctrlKey || e.metaKey)) ||
    // Allow: home, end, left, right, up, down
    (code >= 35 && code <= 40) ||
    // Allow: backspace, tab
    code === 8 ||
    code === 9
  ) {
    return;
  } else {
    e.preventDefault();
  }
};

// Validate number inputs
export const isPositiveInteger = str => {
  return /^[1-9]\d*$/.test(str) && Number(str) > 0;
};

// Validate text inputs
export const validateName = str => {
  return /[!@#$%^&*()+;<>?":{}[\]|]/.test(str);
};

export const compare = (s1, s2) => {
  if (s1 === null && s2 === null) {
    return 0;
  }

  if (s1 === null && s2 !== null) {
    return -1;
  }

  if (s2 === null && s1 !== null) {
    return 1;
  }

  if (!s1.length && !s2.length) {
    return 0;
  }

  let firstMarker = 0;
  let secondMarker = 0;
  let s1Length = s1.length;
  let s2Length = s2.length;

  while (firstMarker < s1Length && secondMarker < s2Length) {
    let thisChunk = breakDown(s1, s1Length, firstMarker);
    firstMarker += thisChunk.length;

    let thatChunk = breakDown(s2, s2Length, secondMarker);
    secondMarker += thatChunk.length;

    // If both contains numeric characters, sort them numerically
    let result = 0;
    if (isDigit(thisChunk.charAt(0)) && isDigit(thatChunk.charAt(0))) {
      let thisChunkLength = thisChunk.length;
      result = thisChunkLength - thatChunk.length;
      if (+result === 0) {
        for (let i = 0; i < thisChunkLength; i++) {
          result = thisChunk.charAt(i) - thatChunk.charAt(i);
          if (+result !== 0) {
            return result;
          }
        }
      }
    } else {
      result = thisChunk.toLowerCase().localeCompare(thatChunk.toLowerCase());
    }

    if (+result !== 0) return result;
  }

  return s1Length - s2Length;
};

function isDigit(c) {
  return /\d/.test(c);
}

const breakDown = (s, slength, marker) => {
  let chunk = "";
  let c = s.charAt(marker);
  chunk += c;
  marker++;
  if (isDigit(c)) {
    while (marker < slength) {
      c = s.charAt(marker);
      if (!isDigit(c)) break;
      chunk += c;
      marker++;
    }
  } else {
    while (marker < slength) {
      c = s.charAt(marker);
      if (isDigit(c)) break;
      chunk += c;
      marker++;
    }
  }
  return chunk;
};

export const isScrolledIntoView = el => {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;
  return elemTop >= 0 && elemBottom <= window.innerHeight;
};

export function exportToXlsx(filename, rows) {
  var wb = XLSX.utils.book_new();
  wb.SheetNames.push(filename.split(".")[0]);

  var ws = XLSX.utils.aoa_to_sheet(rows);
  wb.Sheets[filename.split(".")[0]] = ws;

  var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

  var s2ab = s => {
    var buf = new ArrayBuffer(s.length);
    var view = new Uint8Array(buf);
    for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  };

  var blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export function exportToCsv(filename, rows) {
  console.log(rows, filename);
  var processRow = function (row) {
    var finalVal = "";
    for (var j = 0; j < row.length; j++) {
      var innerValue = row[j] === null ? "" : row[j].toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      }
      var result = innerValue.replace(/"/g, '""');
      // if (result.search(/("|,|\n)/g) >= 0)
      result = '"' + result + '"';
      if (j > 0) finalVal += ",";
      finalVal += result;
    }
    return finalVal + "\n";
  };

  var csvFile = "";
  for (var i = 0; i < rows.length; i++) {
    csvFile += processRow(rows[i]);
  }

  var blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export const getTimeStr = val => {
  val = val ? String(val) : null;
  let result = val;

  if (val && val.indexOf(":") === -1 && val >= 0) {
    try {
      result = secondsToTime(Number(val));
    } catch (error) {
      console.log(error);
    }
  }

  return result;
};

export const generateStopDataForXLSX = (row, index, opts = {}) => {
  console.log(row);
  const infoToReturn = [
    row.type,
    row.information ? row.information.recipient_name : null,
    row.information ? row.information.street : null,
    row.information ? row.information.city : null,
    row.information ? row.information.zipcode : null,
    row.information ? row.information.country : null,
    row.address ? row.address.lat : null,
    row.address ? row.address.lng : null,
    row.duration || "",

    row.time_windows && row.time_windows[0]
      ? row.time_windows[0].earliest
        ? getTimeStr(row.time_windows[0].earliest)
        : null
      : null,

    row.time_windows && row.time_windows[0]
      ? row.time_windows[0].latest
        ? getTimeStr(row.time_windows[0].latest)
        : null
      : null,
    row.information ? row.information.email : null,

    row.information ? row.information.recipient_phone : null,
    row.priority === 1 ? "Yes" : null,
    row.requirements && row.requirements.signature ? "Yes" : null,

    row.requirements && row.requirements.age_verification ? "Yes" : null,
    row.requirements && row.requirements.picture ? "Yes" : null,
    row.information ? row.information.notes : null,

    row.tags
      ?.filter(tag => tag.type === "VEHICLE")
      .map(el => el.name)
      .join("; "),
    row.tags
      ?.filter(tag => tag.type === "DRIVER")
      .map(el => el.name)
      .join("; "),
    row.units && row.units[index]
      ? row.units[index].quantity
      : row.quantity || "",
    row.units && row.units[index] ? row.units[index].weight : row.weight,
    row.units && row.units[index] ? row.units[index].volume : row.volume,
    row.units && row.units[index] ? row.units[index].name : null,
    row.units && row.units[index] ? row.units[index].note : null,
    row.tracking ? row.tracking.id : null
  ];

  if (opts.type === "solution" || opts.type === "routes") {
    infoToReturn.unshift(
      opts.route,
      opts.vehicle,
      opts.driver,
      opts.stop,
      opts.route === "unscheduled"
        ? ""
        : secondsToTime(opts.published ? row.original_arr_time : row.arr_time)
    );
    const birthday =
      row &&
      row.recipient_date_of_birth &&
      new Date(row.recipient_date_of_birth);
    opts.isPastProject &&
      infoToReturn.push(
        opts.project_name,
        opts.project_date,
        opts.route === "unscheduled" || opts.route_started_at === null
          ? ""
          : secondsToTime(opts.route_started_at),
        opts.route === "unscheduled" || opts.route_completed_at === null
          ? ""
          : secondsToTime(opts.route_completed_at),
        opts.route === "unscheduled" ||
          !["finished", "skipped"].includes(row.status)
          ? ""
          : row.status === "finished"
          ? "delivered"
          : row.status,
        opts.route === "unscheduled" || row.finish_time === null
          ? ""
          : secondsToTime(row.finish_time),
        row.recipient_note,
        row.completion_note,
        opts.route === "unscheduled" || opts.planned_distance === null
          ? ""
          : opts.planned_distance,
        birthday
          ? `${birthday.getFullYear()}-${addZero(
              birthday.getMonth() + 1
            )}-${addZero(birthday.getDate())}`
          : "",
        row.signature,
        row.images.join("\n")
      );
  } else if (opts.isPastProject) {
    const birthday =
      row &&
      row.recipient_date_of_birth &&
      new Date(row.recipient_date_of_birth);
    infoToReturn.push(
      !["finished", "skipped"].includes(row.status)
        ? ""
        : row.status === "finished"
        ? "delivered"
        : row.status,
      opts.route === "unscheduled" || row.finish_time === null
        ? ""
        : secondsToTime(row.finish_time),
      row.recipient_note,
      row.completion_note,
      birthday
        ? `${birthday.getFullYear()}-${addZero(
            birthday.getMonth() + 1
          )}-${addZero(birthday.getDate())}`
        : "",
      row.signature || "",
      row.images ? row.images.join("\n") : ""
    );
  }

  return infoToReturn;
};

// Provide custom info, as listview component is the same for drivers and vehicles
export const getDriverInfo = (item, infoNeeded) => {
  switch (infoNeeded) {
    case "left-text":
      return item.first_name + " " + item.last_name;
    case "right-text":
      return item.email;
    case "id":
      return item.id;
    default:
      return "";
  }
};

export const getVehicleInfo = (vehicleType, infoNeeded) => {
  switch (infoNeeded) {
    case "left-text":
      return vehicleType && vehicleType.name;
    case "right-text":
      return "";
    case "id":
      return vehicleType && vehicleType.id;
    default:
      return " ";
  }
};

export const countStops = (routes, liveUpdates) => {
  // Activities count without "start"s and "end"s
  let servicesQuantity = 0;
  let finishedStops = 0;
  let skippedStops = 0;

  routes.forEach(route => {
    const hideLive = route.status === "in_progress" && !liveUpdates;
    route.stops.forEach(stop => {
      if (
        stop.type !== "start" &&
        stop.type !== "end" &&
        stop.type !== "break"
      ) {
        servicesQuantity++;
        if (!hideLive) {
          if (stop.status === "finished") {
            finishedStops++;
          } else if (stop.status === "skipped") {
            skippedStops++;
          }
        }
      }
    });
  });

  return { servicesQuantity, finishedStops, skippedStops };
};

export const isNotEmptyRoute = route =>
  route.stops.some(
    stop =>
      stop.type !== "start" && stop.type !== "end" && stop.type !== "break"
  );
// For route groups search
export const isSearchingValueMatch = (searchingItem, searchingValue) => {
  if (!searchingItem || !searchingValue) {
    return new Error(
      "searchingItem and searchingValue both params should be provided!"
    );
  }

  return searchingItem.toLowerCase().includes(searchingValue.toLowerCase());
};

// export function handleSearchFilterStops(searchValue, dataArr) {
//   const searchDataToReturn = [];
//   const dataToReturn = [];

//   dataToSearch.forEach((strings, i) => {
//     strings.some(str => {
//       if (str.includes(searchValue)) {
//         searchDataToReturn.push(strings);
//         dataToReturn.push(dataArr[i]);
//         return true;
//       }
//     });
//   });

//   return [searchDataToReturn, dataToReturn];
// }

export const completedStopsCount = (stops = []) => {
  return stops.reduce((acc, stop) => {
    if (
      stop.type !== "start" &&
      stop.type !== "end" &&
      stop.type !== "break" &&
      (stop.status === "skipped" || stop.status === "finished")
    ) {
      acc++;
    }
    return acc;
  }, 0);
};

export function debounce(theFunction, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      theFunction.apply(this, args);
    }, timeout);
  };
}

export const mergeReloadStopsToMainRoute = routes => {
  const stopsFromReloads = {};
  const cleanRoutes = routes.filter(route => {
    if (route.vehicle_id.includes("_")) {
      stopsFromReloads[`${route.route_name}`] = Object.keys(
        stopsFromReloads
      ).includes(route.route_name)
        ? [
            ...stopsFromReloads[`${route.route_name}`],
            { ...route.stops[0], type: "reload" },
            ...route.stops.filter(
              stop => !["break", "start", "end"].includes(stop.type)
            )
          ]
        : [
            { ...route.stops[0], type: "reload" },
            ...route.stops.filter(
              stop => !["break", "start", "end"].includes(stop.type)
            )
          ];
      return false;
    }
    return route.selected && !route.vehicle_id.includes("_");
  });

  const mergedRoutes =
    Object.keys(stopsFromReloads).length > 0
      ? cleanRoutes.map(route => {
          const newRoute = { ...route };

          const newStops = [
            newRoute?.stops[0],
            ...newRoute.stops.slice(1, -1),
            ...(stopsFromReloads[`${newRoute.route_name}`] || []),
            newRoute?.stops[newRoute?.stops?.length - 1]
          ];

          newRoute.stops = newStops;

          return newRoute;
        })
      : routes;
  return mergedRoutes;
};

export const checkShouldRouteUnitsBecomeBolder = (unitsCount, isFOJW) => {
  // Check if the unit count exceeds the threshold and if the route is FOJW
  // The 140 threshold is based on requirements
  return unitsCount >= 140 && isFOJW;
};

// Same principle as checkShouldRouteUnitsBecomeBolder, but for the last-customer ETA:
// FOJW flags a route's end time red once the last customer is reached at/after 16:00.
// The threshold lives in routeTime.js (unit-tested); this just adds the FOJW gate.
export const checkShouldRouteEtaBecomeRed = (etaSeconds, isFOJW) =>
  isLastCustomerEtaLate(etaSeconds) && isFOJW;

export const checkIsFOJW = () => {
  return LANDING_URL.includes("fojw");
};
